import { createContext, useContext, useCallback, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Role, Lang, ScreenKey, FeatureFlags, DoaDecision } from '../lib/types';
import { roleMeta, SCREEN_PATH } from '../lib/roles';
import { checkDemoPassword, demoPasswordConfigured } from '../lib/demoAuth';

export type TourFlow = 'procurement' | 'supplier' | 'cco' | 'warehouse' | 'admin';

export interface TourStep {
  /** Screen to navigate to when this step activates (mirrors a User-Flow stage). */
  go?: ScreenKey;
  /** CSS selector to spotlight (sidebar nav items are stable across navigation). */
  sel?: string;
  place?: 'below' | 'right' | 'main';
  title: string;
  body: string;
  titleTh?: string;
  bodyTh?: string;
  /** The manual pain point this step's screen resolves; rendered as its own labelled section above the body. */
  pain?: string;
  painTh?: string;
  centered?: boolean;
  /** Icon shown on a centered finale card. */
  icon?: string;
}

/** The PO the user opened from a list — drives the PO detail screen. */
export interface OpenedPo {
  id: string;
  desc: string;
  sup: string;
  value: string;
  doa: string;
  /** True when the PO is awaiting approval — shows Approve + Sign on the detail. */
  pending: boolean;
  status?: string;
  /** Supplier view: an invoice has already been submitted for this PO. */
  invoiced?: boolean;
  /** Warehouse view: PO creator, for the segregation-of-duties check. */
  createdBy?: string;
}

/** Why the last sign-in attempt was rejected — rendered on the login screen. */
export type LoginError = 'required' | 'invalid' | 'unconfigured' | null;

export interface AppState {
  role: Role;
  lang: Lang;
  loginRole: Role;
  /** Password gate cleared — required to reach any /app screen. */
  authed: boolean;
  /** Password typed on the login screen. */
  pwInput: string;
  loginError: LoginError;
  /** SSO was clicked — the demo has no identity provider, so we say so. */
  ssoNotice: boolean;
  showPw: boolean;
  notifOpen: boolean;
  roleMenuOpen: boolean;
  helpOpen: boolean;
  /** Mobile/tablet off-canvas sidebar drawer (ignored at ≥900px). */
  sidebarOpen: boolean;
  unreadCount: number;
  negotiateOpen: boolean;
  negPrice: string;
  /** Vendor being negotiated with (drives the Negotiate modal). */
  negVendor: { alias: string; premium: number } | null;
  selectedBid: 'A' | 'B' | 'C';
  overrideText: string;
  awardConfirmed: boolean;
  esignOpen: boolean;
  esignStage: 0 | 1 | 2;
  esignConsent: boolean;
  esignPo: string | null;
  doaDecisions: DoaDecision[];
  coiOpen: boolean;
  coiChoice: 'yes' | 'no' | null;
  coiPr: string | null;
  invoiceDocOpen: boolean;
  featureFlags: FeatureFlags;
  regStep: number;
  wbSubmitted: boolean;
  wbAnon: 'anon' | 'named';
  inscTab: 'c1' | 'c2' | 'c3';
  welcomeOpen: boolean;
  ftueDontShow: boolean;
  tourActive: boolean;
  tourFlow: TourFlow;
  tourStep: number;
  tourRect: DOMRectLike | null;
  clOpen: boolean;
  clDismissed: boolean;
  clTour: boolean;
  /** Screens the user has actually opened — drives checklist completion. */
  visited: ScreenKey[];
  bidHintSeen: boolean;
  doaHintSeen: boolean;
  openedPo: OpenedPo | null;
  /** Bid the supplier/insurer opened from Bidding History. */
  openedBid: { bid: string; rfq: string; title: string; amount: string; rank: number; status: string; submitted: string } | null;
}

export interface DOMRectLike {
  top: number; left: number; right: number; bottom: number; width: number; height: number;
}

/**
 * The cleared gate is remembered for the tab only, so a refresh or a deep link
 * into /app during a demo doesn't bounce the presenter back to the login screen.
 */
const SESSION_AUTH_KEY = 'tendra.demo.authed';

function readSessionAuth(): boolean {
  try {
    return sessionStorage.getItem(SESSION_AUTH_KEY) === '1';
  } catch {
    return false; // private-mode / storage disabled — start at the login screen
  }
}

const initialState: AppState = {
  role: 'procurement', lang: 'en', loginRole: 'procurement',
  authed: readSessionAuth(),
  pwInput: '', loginError: null, ssoNotice: false,
  showPw: false, notifOpen: false, roleMenuOpen: false, helpOpen: false, sidebarOpen: false, unreadCount: 3,
  negotiateOpen: false, negPrice: '', negVendor: null,
  selectedBid: 'A', overrideText: '', awardConfirmed: false,
  esignOpen: false, esignStage: 0, esignConsent: false, esignPo: null,
  doaDecisions: [],
  coiOpen: false, coiChoice: null, coiPr: null, invoiceDocOpen: false,
  featureFlags: { aiScoring: true, collusion: true, rpt: true, sod: true, mfa: true, hash: true, onboarding: true },
  regStep: 1, wbSubmitted: false, wbAnon: 'anon', inscTab: 'c1',
  welcomeOpen: false, ftueDontShow: false, tourActive: false, tourFlow: 'procurement', tourStep: 0, tourRect: null,
  clOpen: true, clDismissed: false, clTour: false, visited: [],
  bidHintSeen: false, doaHintSeen: false, openedPo: null, openedBid: null,
};

type Patch = Partial<AppState> | ((prev: AppState) => Partial<AppState>);

export interface AppApi {
  state: AppState;
  /** Pick the string for the current language: L('English', 'ไทย'). */
  L: (en: string, th: string) => string;
  set: (patch: Patch) => void;
  go: (key: ScreenKey) => void;
  /** Verify the typed password; on success land on the selected role's screen. */
  doLogin: () => void;
  doLogout: () => void;
  switchRole: (r: Role) => void;
  toggleLang: () => void;
  openEsign: (po: string) => void;
  closeEsign: () => void;
  finishSign: () => void;
  returnFromSign: () => void;
  startTour: () => void;
  nextStep: () => void;
  prevStep: () => void;
  endTour: () => void;
  finishTour: () => void;
  tourSteps: (flow: TourFlow) => TourStep[];
}

const AppCtx = createContext<AppApi | null>(null);

export function useApp(): AppApi {
  const ctx = useContext(AppCtx);
  if (!ctx) throw new Error('useApp must be used within <AppProvider>');
  return ctx;
}

export function flowForRole(role: Role): TourFlow {
  if (role === 'cco') return 'cco';
  if (role === 'admin') return 'admin';
  if (role === 'warehouse') return 'warehouse';
  if (role === 'insurance' || role === 'supplier') return 'supplier';
  return 'procurement';
}

/**
 * Guided-tour scripts, one per role, tracing the AI-BID User Flows end to end
 * (see [EN] AI_BID_Requirements_v1_0 · "User Flows"). Each step navigates the main
 * pane to the relevant screen and spotlights its sidebar entry.
 */
const TOURS: Record<TourFlow, TourStep[]> = {
  // FLOW-B1 (COI) → FLOW-C1 (RFQ) → FLOW-E1 (PO/GR/3-way) → FLOW-C2 (Insurance) → FLOW-A1 (ASL review)
  procurement: [
    { go: 'dashboard', sel: '[data-tour="sidebar"]', place: 'right', title: 'Your workspace', body: 'Work is grouped into Procurement, Suppliers, and Approval. This tour walks the whole source-to-pay journey, step by step.', titleTh: 'พื้นที่ทำงานของคุณ', bodyTh: 'งานถูกจัดกลุ่มเป็น จัดซื้อ, ซัพพลายเออร์ และการอนุมัติ ทัวร์นี้จะพาคุณเดินทางจากการจัดหาจนถึงการชำระเงินทั้งหมด ทีละขั้นตอน' },
    { go: 'dashboard', sel: '[data-tour="kpis"]', place: 'below', title: 'Your day at a glance', body: 'Live counts for incoming PRs, active RFQs, pending approvals, and supplier reviews. Click any card to jump to the queue behind it.', titleTh: 'ภาพรวมงานของคุณในวันนี้', bodyTh: 'จำนวนแบบเรียลไทม์ของใบขอซื้อ (PR) ที่เข้ามา, ใบขอเสนอราคา (RFQ) ที่ดำเนินอยู่, การอนุมัติที่รอดำเนินการ และการตรวจสอบซัพพลายเออร์ คลิกที่การ์ดใดก็ได้เพื่อไปยังคิวที่อยู่เบื้องหลัง' },
    { go: 'incoming', sel: '[data-tour="main"]', place: 'main', title: '1 · Requisitions arrive', pain: 'Conflicts of interest were checked informally, if at all.', painTh: 'ตรวจความขัดแย้งทางผลประโยชน์แบบไม่เป็นทางการ หรือไม่ตรวจเลย', body: 'PRs flow in from the ERP and the threshold sets the procurement method. A COI declaration is mandatory before you can review — every time.', titleTh: '1 · ใบขอซื้อเข้ามา', bodyTh: 'ใบขอซื้อ (PR) ไหลเข้ามาจาก ERP และเกณฑ์วงเงินจะกำหนดวิธีการจัดซื้อ ต้องแจ้ง COI ก่อนตรวจสอบทุกครั้งอย่างเป็นข้อบังคับ' },
    { go: 'rfq', sel: '[data-tour="main"]', place: 'main', title: '2 · Send the RFQ', pain: 'Suppliers were picked by hand.', painTh: 'เลือกซัพพลายเออร์ด้วยตนเอง', body: 'AI auto-ranks the top 5 from the Approved Supplier List. Confirm them, set a deadline, and send blind RFQs by portal and email.', titleTh: '2 · ส่งใบขอเสนอราคา', bodyTh: 'AI จัดอันดับ 5 อันดับแรกจากรายชื่อผู้ขายที่อนุมัติให้อัตโนมัติ ยืนยันรายชื่อ, กำหนดกำหนดส่ง และส่งใบขอเสนอราคา (RFQ) แบบปิดผ่านพอร์ทัลและอีเมล' },
    { go: 'bidcompare', sel: '[data-tour="main"]', place: 'main', title: '3 · Sealed, blind bids', pain: 'Bids were compared in spreadsheets, by gut feel.', painTh: 'เทียบข้อเสนอในสเปรดชีตตามความรู้สึก', body: 'Every bid is sealed and timestamped — no one sees prices early. At the deadline they decrypt simultaneously and AI scores each against the last purchase price.', titleTh: '3 · ข้อเสนอแบบปิดผนึก', bodyTh: 'ทุกข้อเสนอถูกปิดผนึกและประทับเวลา — ไม่มีใครเห็นราคาก่อนกำหนด เมื่อถึงกำหนดส่ง ข้อเสนอจะถูกถอดรหัสพร้อมกัน และ AI ให้คะแนนแต่ละข้อเสนอเทียบกับราคาซื้อครั้งล่าสุด' },
    { go: 'award', sel: '[data-tour="main"]', place: 'main', title: '4 · Decide the award', pain: 'Awards went to gut-feel favourites, with no explanation on file.', painTh: 'มอบสัญญาตามความรู้สึกและเจ้าประจำ โดยไม่มีคำอธิบายบันทึกไว้', body: 'Accept the AI recommendation, Negotiate, or Override with a ≥50-character justification (that triggers an extra approval). Identities are revealed only after you confirm.', titleTh: '4 · ตัดสินการมอบสัญญา', bodyTh: 'ยอมรับคำแนะนำของ AI, เจรจา หรือแทนที่ด้วยคำอธิบายเหตุผลอย่างน้อย 50 อักขระ (ซึ่งจะทำให้ต้องมีการอนุมัติเพิ่ม) ตัวตนจะถูกเปิดเผยหลังจากที่คุณยืนยันแล้วเท่านั้น' },
    { go: 'doa', sel: '[data-tour="main"]', place: 'main', title: '5 · Approve & e-sign', pain: 'Paper sign-offs were chased desk to desk.', painTh: 'ไล่ตามลายเซ็นบนกระดาษโต๊ะต่อโต๊ะ', body: 'The award routes up the Delegation-of-Authority matrix and is signed electronically. The PO is then created automatically in the ERP.', titleTh: '5 · อนุมัติและลงนามอิเล็กทรอนิกส์', bodyTh: 'การมอบสัญญาจะถูกส่งขึ้นตามเมทริกซ์การมอบอำนาจ (DOA) และลงนามอิเล็กทรอนิกส์ จากนั้น PO จะถูกสร้างขึ้นโดยอัตโนมัติใน ERP' },
    { go: 'invoice', sel: '[data-tour="main"]', place: 'main', title: '6 · Receive & 3-way match', pain: 'Separation of duties relied on trust, not the system.', painTh: 'การแบ่งแยกหน้าที่อาศัยความไว้ใจ ไม่ใช่ระบบ', body: 'The system enforces it: goods are received by someone other than the orderer. PO × Goods Receipt × Invoice must reconcile before anything reaches AP.', titleTh: '6 · รับสินค้าและการจับคู่ 3 ทาง', bodyTh: 'ระบบบังคับใช้ให้: สินค้าจะต้องรับโดยบุคคลอื่นที่ไม่ใช่ผู้สั่งซื้อ PO × ใบรับสินค้า × ใบแจ้งหนี้ ต้องกระทบยอดตรงกันก่อนที่จะส่งต่อไปยัง AP' },
    { go: 'batch', sel: '[data-tour="main"]', place: 'main', title: 'Insurance, in one batch', pain: 'Insurance renewals were handled one policy at a time, by hand.', painTh: 'ต่อประกันด้วยมือทีละกรมธรรม์', body: 'Renew the fleet at once: group vehicles by class, invite insurers together, and compare premium, excess, coverage, and claim speed per class.', titleTh: 'ประกันภัย ในกลุ่มเดียว', bodyTh: 'ต่ออายุยานพาหนะทั้งกองพร้อมกัน: จัดกลุ่มยานพาหนะตามประเภท, เชิญผู้รับประกันภัยพร้อมกัน และเปรียบเทียบเบี้ยประกัน, ค่าเสียหายส่วนแรก, ความคุ้มครอง และความเร็วในการเคลมของแต่ละประเภท' },
    { go: 'asl', sel: '[data-tour="main"]', place: 'main', title: 'Suppliers stay clean', pain: 'Supplier vetting was informal, with no consistent evaluation.', painTh: 'คัดกรองซัพพลายเออร์แบบไม่เป็นทางการ ไม่มีเกณฑ์ประเมินที่สม่ำเสมอ', body: 'New suppliers are DBD-verified and COI-checked, then tiered A/B/C. The red badge on Supplier Review means registrations are waiting for you.', titleTh: 'ซัพพลายเออร์สะอาดเสมอ', bodyTh: 'ซัพพลายเออร์รายใหม่จะถูกตรวจสอบกับ DBD และตรวจ COI จากนั้นจัดระดับเป็น A/B/C ป้ายสีแดงบนการตรวจสอบซัพพลายเออร์หมายความว่ามีการลงทะเบียนรอคุณอยู่' },
    { centered: true, icon: 'party-popper', title: "You're all set.", body: "That's the full journey — intake, blind bidding, approval, and 3-way match. The checklist bottom-right will guide your first pass.", titleTh: 'พร้อมแล้ว.', bodyTh: 'นี่คือการเดินทางทั้งหมด — การรับงาน, การประมูลแบบปิด, การอนุมัติ และการจับคู่ 3 ทาง เช็กลิสต์ที่มุมขวาล่างจะแนะนำคุณในรอบแรก' },
  ],
  // FLOW-A1 (supplier side) → FLOW-C1 (bidding) → FLOW-E1 (orders & invoices)
  supplier: [
    { go: 'myboard', sel: '[data-tour="invites"]', place: 'below', title: 'Your invitations', body: "RFQ invitations land here. Anything marked 'Awaiting Bid' has a deadline — submit before it closes.", titleTh: 'คำเชิญของคุณ', bodyTh: "คำเชิญ RFQ จะมาที่นี่ รายการที่ระบุว่า 'Awaiting Bid' จะมีกำหนดส่ง — ส่งก่อนที่จะปิด" },
    { go: 'bidsubmit', sel: '[data-tour="main"]', place: 'main', title: 'Submit a sealed bid', pain: 'Winning depended on relationships, not the best offer.', painTh: 'การชนะขึ้นอยู่กับความสัมพันธ์ ไม่ใช่ข้อเสนอที่ดีที่สุด', body: 'Bids are blind — your identity stays masked until the award is decided. Respond by web form or upload a document (we OCR it).', titleTh: 'ส่งข้อเสนอแบบปิดผนึก', bodyTh: 'ข้อเสนอเป็นแบบปิด — ตัวตนของคุณจะถูกปกปิดไว้จนกว่าจะตัดสินการมอบสัญญา ตอบกลับผ่านแบบฟอร์มเว็บหรืออัปโหลดเอกสาร (เราจะทำ OCR ให้)' },
    { go: 'myorders', sel: '[data-tour="main"]', place: 'main', title: 'Won? Deliver & invoice', body: 'Awards become Purchase Orders here. Upload invoices against them and VAT is calculated for you, ready for the 3-way match.', titleTh: 'ชนะแล้ว? ส่งมอบและออกใบแจ้งหนี้', bodyTh: 'การมอบสัญญาจะกลายเป็นใบสั่งซื้อ (PO) ที่นี่ อัปโหลดใบแจ้งหนี้ผูกกับใบสั่งซื้อ แล้วระบบจะคำนวณ VAT ให้คุณ พร้อมสำหรับการจับคู่ 3 ทาง' },
    { go: 'myboard', sel: '[data-tour="mykpis"]', place: 'below', title: 'Your reputation, scored', body: 'On-time delivery and quality feed your Performance Score — it shapes your AI ranking on future bids.', titleTh: 'ชื่อเสียงของคุณ ที่ถูกให้คะแนน', bodyTh: 'การส่งมอบตรงเวลาและคุณภาพจะป้อนเข้าสู่คะแนนผลงานของคุณ — ซึ่งกำหนดอันดับ AI ของคุณในการเสนอราคาครั้งต่อไป' },
    { centered: true, icon: 'party-popper', title: "You're all set.", body: 'Watch for invitations, bid before the deadline, and keep your performance score high.', titleTh: 'พร้อมแล้ว.', bodyTh: 'คอยดูคำเชิญ, เสนอราคาก่อนกำหนดส่ง และรักษาคะแนนผลงานของคุณให้สูงไว้' },
  ],
  // FLOW-D1 (fraud detection) + audit
  cco: [
    { go: 'exec', sel: '[data-tour="main"]', place: 'main', title: 'The executive view', body: 'Spend, savings versus last-purchase-price, and the live compliance exceptions — all in one place.', titleTh: 'มุมมองผู้บริหาร', bodyTh: 'ค่าใช้จ่าย, การประหยัดเทียบกับราคาซื้อครั้งล่าสุด และข้อยกเว้นด้านการปฏิบัติตามกฎแบบเรียลไทม์ — ทั้งหมดในที่เดียว' },
    { go: 'fraud', sel: '[data-tour="main"]', place: 'main', title: 'Fraud runs in the background', pain: 'Fraud and collusion went undetected.', painTh: 'ทุจริตและการสมรู้ร่วมคิดไม่ถูกตรวจพบ', body: 'On every RFQ, PR, and PO the AI scores collusion (5 patterns), split orders, and related-party transactions — and tracks each officer’s override rate (alert above 10%).', titleTh: 'การตรวจจับทุจริตทำงานเบื้องหลัง', bodyTh: 'ในทุก RFQ, PR และ PO นั้น AI จะให้คะแนนการสมรู้ร่วมคิด (5 รูปแบบ), การแบ่งย่อยคำสั่งซื้อ และธุรกรรมของบุคคลที่เกี่ยวโยงกัน — และติดตามอัตราการแทนที่ของเจ้าหน้าที่แต่ละคน (แจ้งเตือนเมื่อเกิน 10%)' },
    { go: 'network', sel: '[data-tour="main"]', place: 'main', title: 'See the connections', pain: 'Shared directors were invisible in a filing cabinet.', painTh: 'กรรมการที่ใช้ร่วมกันมองไม่เห็นในตู้เอกสาร', body: 'The supplier network graph surfaces them; the RPT report and override log give you the paper trail behind each flag.', titleTh: 'ดูความเชื่อมโยง', bodyTh: 'กราฟเครือข่ายซัพพลายเออร์เผยให้เห็นความเชื่อมโยงเหล่านั้น; รายงาน RPT และบันทึกการแทนที่ให้หลักฐานเอกสารเบื้องหลังการแจ้งเตือนแต่ละรายการ' },
    { go: 'audit', sel: '[data-tour="main"]', place: 'main', title: 'Provable forever', pain: 'No immutable trail for SEC / IPO diligence.', painTh: 'ไม่มีบันทึกที่เปลี่ยนแปลงไม่ได้สำหรับการสอบทาน ก.ล.ต. / IPO', body: 'Every action is written to a hash-verified audit trail you can export and reconcile line by line.', titleTh: 'พิสูจน์ได้ตลอดไป', bodyTh: 'ทุกการกระทำถูกบันทึกลงในบันทึกการตรวจสอบที่ยืนยันด้วยแฮช ซึ่งคุณสามารถส่งออกและกระทบยอดได้ทีละบรรทัด' },
    { centered: true, icon: 'party-popper', title: "You're all set.", body: 'Exceptions surface here automatically — review the flagged items each month.', titleTh: 'พร้อมแล้ว.', bodyTh: 'ข้อยกเว้นจะปรากฏที่นี่โดยอัตโนมัติ — ตรวจสอบรายการที่ถูกแจ้งเตือนในแต่ละเดือน' },
  ],
  // FLOW-E1 (goods receipt, Segregation of Duties)
  warehouse: [
    { go: 'whpo', sel: '[data-tour="main"]', place: 'main', title: 'Receive goods here', pain: 'Segregation of Duties relied on habit, not the system.', painTh: 'การแบ่งแยกหน้าที่อาศัยความเคยชิน ไม่ใช่ระบบ', body: 'Open a purchase order and record the Goods Receipt; the system enforces that the receiver differs from the orderer.', titleTh: 'รับสินค้าที่นี่', bodyTh: 'เปิดใบสั่งซื้อและบันทึกใบรับสินค้า ระบบบังคับให้ผู้รับต้องไม่ใช่คนเดียวกับผู้สั่งซื้อ' },
    { centered: true, icon: 'check-circle', title: "That's it.", body: "Once goods are received, procurement's 3-way match and payment can proceed.", titleTh: 'เท่านี้แหละ.', bodyTh: 'เมื่อรับสินค้าแล้ว การจับคู่ 3 ทางและการชำระเงินของฝ่ายจัดซื้อก็สามารถดำเนินการต่อได้' },
  ],
  // System configuration + oversight
  admin: [
    { go: 'exec', sel: '[data-tour="main"]', place: 'main', title: 'Platform overview', body: 'Spend and compliance at a glance across the whole organisation.', titleTh: 'ภาพรวมแพลตฟอร์ม', bodyTh: 'ค่าใช้จ่ายและการปฏิบัติตามกฎในภาพรวมทั่วทั้งองค์กร' },
    { go: 'users', sel: '[data-tour="main"]', place: 'main', title: 'People & access', body: 'Manage users and roles. MFA is enforced for privileged roles.', titleTh: 'ผู้คนและสิทธิ์การเข้าถึง', bodyTh: 'จัดการผู้ใช้และบทบาท มีการบังคับใช้ MFA สำหรับบทบาทที่มีสิทธิ์พิเศษ' },
    { go: 'settings', sel: '[data-tour="main"]', place: 'main', title: 'Tune the rules', pain: 'Everyone evaluated differently — no single ruleset.', painTh: 'แต่ละคนประเมินไม่เหมือนกัน — ไม่มีกฎชุดเดียว', body: 'Set one explainable ruleset here — DOA thresholds, AI scoring weights, and toggles for collusion detection and RPT flags.', titleTh: 'ปรับกฎเกณฑ์', bodyTh: 'ตั้งกฎชุดเดียวที่อธิบายได้ที่นี่ — เกณฑ์วงเงินของ DOA และน้ำหนักการให้คะแนนของ AI และเปิดปิดฟีเจอร์ต่าง ๆ เช่น การตรวจจับการสมรู้ร่วมคิดและการแจ้งเตือน RPT' },
    { go: 'audit', sel: '[data-tour="main"]', place: 'main', title: 'Everything is logged', pain: 'Changes were untraceable after the fact.', painTh: 'การเปลี่ยนแปลงตรวจสอบย้อนหลังไม่ได้', body: 'Configuration changes and every action land in the hash-verified audit trail.', titleTh: 'ทุกอย่างถูกบันทึก', bodyTh: 'การเปลี่ยนแปลงการตั้งค่าและทุกการกระทำจะถูกบันทึกลงในบันทึกการตรวจสอบที่ยืนยันด้วยแฮช' },
    { centered: true, icon: 'party-popper', title: "You're all set.", body: 'Thresholds, weights, and flags are yours to tune — all changes are audited.', titleTh: 'พร้อมแล้ว.', bodyTh: 'เกณฑ์วงเงิน, น้ำหนัก และการแจ้งเตือนเป็นของคุณที่จะปรับแต่ง — ทุกการเปลี่ยนแปลงถูกบันทึกการตรวจสอบ' },
  ],
};

function tourStepsFor(flow: TourFlow): TourStep[] {
  return TOURS[flow];
}

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AppState>(initialState);
  const stateRef = useRef(state);
  stateRef.current = state;
  const navigate = useNavigate();
  const esignTimer = useRef<number | null>(null);

  const set = useCallback((patch: Patch) => {
    setState((prev) => ({ ...prev, ...(typeof patch === 'function' ? patch(prev) : patch) }));
  }, []);

  const go = useCallback((key: ScreenKey) => {
    setState((prev) => ({
      ...prev,
      notifOpen: false, roleMenuOpen: false, coiOpen: false, invoiceDocOpen: false, negotiateOpen: false, helpOpen: false,
      visited: prev.visited.includes(key) ? prev.visited : [...prev.visited, key],
    }));
    navigate(SCREEN_PATH[key]);
  }, [navigate]);

  const doLogin = useCallback(() => {
    const typed = stateRef.current.pwInput.trim(); // tolerate a pasted password with stray whitespace
    if (!demoPasswordConfigured) return set({ loginError: 'unconfigured', ssoNotice: false });
    if (!typed) return set({ loginError: 'required', ssoNotice: false });
    if (!checkDemoPassword(typed)) return set({ loginError: 'invalid', ssoNotice: false });

    try { sessionStorage.setItem(SESSION_AUTH_KEY, '1'); } catch { /* non-fatal: gate just won't survive a refresh */ }
    setState((prev) => ({
      ...prev,
      authed: true, pwInput: '', loginError: null, ssoNotice: false, showPw: false,
      role: prev.loginRole, welcomeOpen: prev.featureFlags.onboarding,
    }));
    navigate(SCREEN_PATH[roleMeta(stateRef.current.loginRole).land]);
  }, [navigate, set]);

  const doLogout = useCallback(() => {
    try { sessionStorage.removeItem(SESSION_AUTH_KEY); } catch { /* ignore */ }
    setState((prev) => ({ ...prev, authed: false, notifOpen: false, roleMenuOpen: false }));
    navigate('/login');
  }, [navigate]);

  const switchRole = useCallback((r: Role) => {
    setState((prev) => ({ ...prev, role: r, roleMenuOpen: false, coiOpen: false, invoiceDocOpen: false, negotiateOpen: false }));
    navigate(SCREEN_PATH[roleMeta(r).land]);
  }, [navigate]);

  const toggleLang = useCallback(() => set((s) => ({ lang: s.lang === 'th' ? 'en' : 'th' })), [set]);

  const openEsign = useCallback((po: string) => {
    set({ esignOpen: true, esignStage: 0, esignConsent: false, esignPo: po });
    if (esignTimer.current) window.clearTimeout(esignTimer.current);
    esignTimer.current = window.setTimeout(() => {
      setState((prev) => (prev.esignOpen ? { ...prev, esignStage: 1 } : prev));
    }, 1600);
  }, [set]);

  const closeEsign = useCallback(() => set({ esignOpen: false }), [set]);
  const finishSign = useCallback(() => setState((prev) => (prev.esignConsent ? { ...prev, esignStage: 2 } : prev)), []);

  const returnFromSign = useCallback(() => {
    setState((prev) => {
      const po = prev.esignPo || 'PO-2026-0041';
      const env = 'ESC-' + Math.random().toString(16).slice(2, 10).toUpperCase();
      const time = '14:0' + (prev.doaDecisions.length + 2) + ':11';
      return { ...prev, esignOpen: false, doaDecisions: [{ id: po, envelope: env, time }, ...prev.doaDecisions] };
    });
  }, []);

  // ---- Guided tour ----
  const startTour = useCallback(() => {
    const flow = flowForRole(stateRef.current.role);
    const first = TOURS[flow][0];
    navigate(SCREEN_PATH[first.go ?? roleMeta(stateRef.current.role).land]);
    setState((prev) => ({ ...prev, tourActive: true, tourFlow: flow, tourStep: 0, tourRect: null, welcomeOpen: false, helpOpen: false, notifOpen: false, roleMenuOpen: false }));
  }, [navigate]);

  const endTour = useCallback(() => set({ tourActive: false }), [set]);

  const finishTour = useCallback(() => {
    try { localStorage.setItem('tendra_ftue_done', '1'); } catch { /* ignore */ }
    set({ tourActive: false, clTour: true, clDismissed: false, clOpen: true });
  }, [set]);

  const nextStep = useCallback(() => {
    const { tourFlow, tourStep } = stateRef.current;
    const steps = TOURS[tourFlow];
    if (tourStep >= steps.length - 1) {
      try { localStorage.setItem('tendra_ftue_done', '1'); } catch { /* ignore */ }
      setState((prev) => ({ ...prev, tourActive: false, clTour: true, clDismissed: false, clOpen: true }));
      return;
    }
    const next = steps[tourStep + 1];
    if (next.go) navigate(SCREEN_PATH[next.go]);
    setState((prev) => ({ ...prev, tourStep: prev.tourStep + 1, tourRect: null }));
  }, [navigate]);

  const prevStep = useCallback(() => {
    const { tourFlow, tourStep } = stateRef.current;
    if (tourStep <= 0) return;
    const prevS = TOURS[tourFlow][tourStep - 1];
    if (prevS.go) navigate(SCREEN_PATH[prevS.go]);
    setState((prev) => ({ ...prev, tourStep: Math.max(0, prev.tourStep - 1), tourRect: null }));
  }, [navigate]);

  const L = useCallback((en: string, th: string) => (stateRef.current.lang === 'th' ? th : en), []);

  const api = useMemo<AppApi>(() => ({
    state, L, set, go, doLogin, doLogout, switchRole, toggleLang,
    openEsign, closeEsign, finishSign, returnFromSign,
    startTour, nextStep, prevStep, endTour, finishTour,
    tourSteps: tourStepsFor,
  }), [state, L, set, go, doLogin, doLogout, switchRole, toggleLang, openEsign, closeEsign, finishSign, returnFromSign, startTour, nextStep, prevStep, endTour, finishTour]);

  return <AppCtx.Provider value={api}>{children}</AppCtx.Provider>;
}
